"use client";

import { useCallback, useEffect, useState } from "react";
import { PiXBold } from "react-icons/pi";
import { Controller, SubmitHandler, UseFormReturn } from "react-hook-form";
import { Form } from "@/components/ui/form";
import { Input, Button, ActionIcon, Title, Select } from "rizzui";
import { useModal } from "@/app/shared/modal-views/use-modal";
import { useNavigate } from "react-router-dom";
import UserRepository from "@/utilities/repositories/Users";
import { IUserData, Roles } from "@/utilities/types/Users";
import toast from "react-hot-toast";
import useApi from "@/hooks/useApi";
import { IResponseData } from "@/utilities/types/requests";
import { filterChangedFormFields } from "@/utils/form";
import {
  EditUserSchema,
  editUserSchema,
} from "@/utils/validators/edit-user.schema";
import useProfile from "@/hooks/use-profile";
import SingleImageUpload from "@/components/ui/file-upload/single-image-upload.tsx";
import { backPrev } from "@/utilities/functions";
import SettingsRepository from "@/utilities/repositories/Settings";
import SelectDomain, {
  SelectOptionType,
} from "@/app/app-shared/user/users-table/select-domain.tsx";
import { IDomainDTO, TAgencySetting } from "@/utilities/types/Setting.ts";
import formatPhoneNumber from "@/utilities/functions/format-phone-number";
import { COUNTRY_SG_CODE } from "@/config/constants";
import { findFeatureByType } from "@/app/app-shared/advanced-features/ultil";
import { EFeatureType } from "@/utilities/types/AdvanceFeature";
import { EDefaultUserSettingRoute } from "@/utilities/enums/DefaultUserSetting";

export default function EditUser({
  id,
  title,
  backTo,
}: {
  id: string;
  title: string;
  backTo?: string;
}) {
  const { closeModal, isOpen } = useModal();
  const [reset, setReset] = useState<{
    firstName: string;
    lastName: string;
    password: string;
    email: string;
    phone: string;
    photo: { url: string } | null;
    photoId: string;
    // measuringId?: string | null;
    headerScript?: string;
    bodyScript?: string;
    enquiryScript?: string;
    domain?: string | null;
    adRerportUrl?: string | null;
    whatsapp?: string;
    registrationNumber?: string;
    enabledAutoAddNewProject?: {
      enable: boolean;
      type: EFeatureType.AutoAddNewProject;
    };
  }>({
    firstName: "",
    lastName: "",
    password: "",
    email: "",
    phone: "",
    photo: null,
    photoId: "",
    adRerportUrl: "",
    whatsapp: "",
    registrationNumber: "",
    // measuringId: "",
    // headerScript: "",
    // bodyScript: "",
    // enquiryScript: "",
    enabledAutoAddNewProject: {
      enable: false,
      type: EFeatureType.AutoAddNewProject,
    },
  });

  const [mapOptions, setMapOptions] = useState<SelectOptionType[]>([]);
  const [defaultOptions, setDefaultOptions] = useState<SelectOptionType | null>(
    null,
  );
  const [configID, setConfigID] = useState<IDomainDTO[]>();
  const [userHasDirectoryPermission, setUserHasDirectoryPermission] =
    useState(false);

  const navigate = useNavigate();

  const { profile, setProfile } = useProfile();
  const [userEdit, setUserEdit] = useState<IUserData>();

  const isMyProfile = profile?.id === id;
  const isAgencyEditUser =
    profile?.role === Roles.AGENCY && userEdit?.role === Roles.USER;
  const isAgencyEditingOwnProfile =
    profile?.role === Roles.AGENCY && isMyProfile;

  const [isLoading, setIsLoading] = useState(false);

  const [defaultHomeRoute, setDefaultHomeRoute] = useState<{
    label: string;
    value: EDefaultUserSettingRoute;
  }>(
    profile?.defaultHomeRoute === EDefaultUserSettingRoute.ALL_LEADS
      ? {
          label: "All Leads",
          value: EDefaultUserSettingRoute.ALL_LEADS,
        }
      : {
          label: "Dashboard",
          value: EDefaultUserSettingRoute.DASHBOARD,
        },
  );

  const [form, setForm] =
    useState<UseFormReturn<EditUserSchema, any, undefined>>();
  const handleBack = () => {
    if (!backTo) return;
    navigate(backPrev(backTo));
    closeModal?.();
  };

  const { request: findUserDetail, response } = useApi<
    IResponseData<IUserData>
  >({
    request: UserRepository.getUserById,
  });

  const { request: findMeDetails } = useApi<IResponseData<IUserData>>({
    request: UserRepository.getMe,
  });

  const refetchDomains = useCallback(async () => {
    try {
      const res = isMyProfile
        ? await UserRepository.getMe()
        : await UserRepository.getUserById(id);

      if (res?.data?.config) {
        setOptionsSelect(res.data.config);
      }
    } catch (error) {
      console.error("Failed to refetch domains:", error);
    }
  }, [id, isMyProfile]);

  const updateUser = async (dataForm: EditUserSchema) => {
    if (!id || !form?.formState.dirtyFields) return;

    const fieldChange = filterChangedFormFields(
      dataForm,
      form?.formState.dirtyFields,
    );

    const { domain, ...userData } = fieldChange;

    if (userData.photo && userData.photo.id === "" && !isAgencyEditUser) {
      // Handle photo deletion for updateUser function
      delete userData.photo;
    }

    try {
      setIsLoading(true);

      // Prepare photoId based on photo changes
      let photoIdToSend: string | null = userEdit?.photoId || null;
      if (userData.photo) {
        if (userData.photo.id) {
          // New photo uploaded
          photoIdToSend = userData.photo.id;
        } else if (userData.photo.id === "") {
          // Photo deleted - send null to remove
          photoIdToSend = null;
          delete userData.photo;
        }
      }

      await UserRepository.updateUser(id, {
        ...userData,
        enabledAutoAddNewProject: userData.enabledAutoAddNewProject?.enable,
        phone: dataForm.phone
          ? COUNTRY_SG_CODE +
            formatPhoneNumber(dataForm.phone).trim().split(" ").join("")
          : "",
        whatsapp: !isAgencyEditUser
          ? undefined
          : dataForm.whatsapp
            ? COUNTRY_SG_CODE +
              formatPhoneNumber(dataForm.whatsapp).trim().split(" ").join("")
            : "",
        registrationNumber: !isAgencyEditUser
          ? undefined
          : dataForm.registrationNumber,
        photoId: isAgencyEditUser ? photoIdToSend : undefined,
      });

      if (domain && response?.data?.config?.id) {
        await SettingsRepository.createDomain({
          configId: response?.data?.config?.id,
          name: domain?.toLowerCase() || "",
          primary: true,
        });
      }
      toast.success(`Success`);
      setTimeout(() => {
        setIsLoading(false);

        if (backTo) {
          setReset({
            firstName: "",
            lastName: "",
            password: "",
            email: "",
            phone: "",
            photo: null,
            photoId: "",
            adRerportUrl: "",
            enabledAutoAddNewProject: {
              enable: false,
              type: EFeatureType.AutoAddNewProject,
            },
          });
        }

        if (isMyProfile && profile) {
          setProfile({
            ...profile,
            ...form.getValues(),
          } as IUserData);
        }

        handleBack();
      }, 600);
    } catch (error: any) {
      toast.error(error?.message || "Something went wrong");
      setIsLoading(false);
    }
  };

  const updateMe = async (dataForm: EditUserSchema) => {
    try {
      setIsLoading(true);
      const dataSubmit: any = {
        email: dataForm.email,
        firstName: dataForm.firstName,
        lastName: dataForm.lastName,
        phone: dataForm.phone
          ? COUNTRY_SG_CODE +
            formatPhoneNumber(dataForm.phone).trim().split(" ").join("")
          : "",
        whatsapp: isAgencyEditingOwnProfile
          ? undefined
          : dataForm.whatsapp
            ? COUNTRY_SG_CODE +
              formatPhoneNumber(dataForm.whatsapp).trim().split(" ").join("")
            : "",
        registrationNumber: isAgencyEditingOwnProfile
          ? undefined
          : dataForm.registrationNumber || "",
      };

      // Handle photo properly: update existing photo or delete photo
      if (dataForm?.photo) {
        if (dataForm.photo.id) {
          // Case: Update existing photo
          dataSubmit.photoId = dataForm.photo.id;
        } else if (dataForm.photo.id === "") {
          // Case: Delete photo (send null to remove)
          dataSubmit.photoId = null;
        }
      }

      const response = await UserRepository.updateMe({
        ...dataSubmit,
      });
      toast.success(`Success`);
      setIsLoading(false);

      if (backTo) {
        setReset({
          firstName: "",
          lastName: "",
          password: "",
          email: "",
          phone: "",
          photo: null,
          photoId: "",
          adRerportUrl: "",
          whatsapp: "",
          registrationNumber: "",
          enabledAutoAddNewProject: {
            enable: false,
            type: EFeatureType.AutoAddNewProject,
          },
        });
      }

      if (isMyProfile && profile) {
        const profileData = {
          ...profile,
          ...response?.data,
        };
        setProfile({
          ...profileData,
        });
      }

      handleBack();
    } catch (error: any) {
      toast.error(error?.message || "Something went wrong");
      setIsLoading(false);
    }
  };
  const onSubmit: SubmitHandler<EditUserSchema> = (data) => {
    if (!isMyProfile) {
      updateUser(data);
    } else if (isMyProfile) {
      updateMe(data);
    }
  };

  const setOptionsSelect = (config: TAgencySetting) => {
    if (config) {
      const primaryDomain = config?.domains?.find((item) => !!item.primary);
      primaryDomain &&
        setDefaultOptions({
          primary: primaryDomain.primary,
          value: primaryDomain.id,
          label: `${primaryDomain.name?.toLowerCase()}`,
          configID: primaryDomain.configId,
        });
      if (config?.domains) {
        setConfigID(config.domains);
        const mapOptionsArr = config.domains.map((item) => {
          return {
            primary: item.primary,
            value: item.id,
            label: item.name?.toLowerCase(),
            configID: item.configId,
          };
        });
        setMapOptions(mapOptionsArr);
      }
    }
  };

  const handleSetDefaultRoute = useCallback(
    async (page: EDefaultUserSettingRoute) => {
      if (page === EDefaultUserSettingRoute.ALL_LEADS) {
        try {
          const toastID = toast.loading("All Leads as home page...");
          const userUpdated = await UserRepository.updateMe({
            defaultHomeRoute: EDefaultUserSettingRoute.ALL_LEADS,
          });
          if (userUpdated?.data) {
            setProfile(userUpdated.data);
          }
          toast.success("All Leads as home page successfully", { id: toastID });
        } catch (error) {
          toast.error("Failed to set as home page");
          console.log(error);
        }
      } else {
        try {
          const toastID = toast.loading("Dashboard as home page...");
          const userUpdated = await UserRepository.updateMe({
            defaultHomeRoute: null,
          });
          if (userUpdated?.data) {
            setProfile(userUpdated.data);
          }
          toast.success("Dashboard as home page successfully", { id: toastID });
        } catch (error) {
          toast.error("Failed to set as home page");
          console.log(error);
        }
      }
    },
    [],
  );

  useEffect(() => {
    if (!id) return;
    if (isMyProfile) {
      findMeDetails().then((res) => {
        if (!res?.data) return;
        const {
          firstName,
          lastName,
          phone,
          email,
          photo,
          photoId,
          config,
          userFeatures,
          whatsapp,
          role,
        } = res.data;
        let photoArgs = {
          width: 0,
          url: "",
        };
        if (photo?.urls?.[0]) {
          photoArgs = photo?.urls?.[0];
        }
        if (config) setOptionsSelect(config);

        const userFeatureAutoAddNewProject = userFeatures?.find(
          (userFeature) =>
            userFeature.feature.type === EFeatureType.AutoAddNewProject,
        );
        const whatsappFinally =
          role === Roles.USER ? config?.whatsapp : whatsapp;
        // @ts-ignore
        setReset({
          firstName,
          lastName,
          password: "",
          email,
          phone: phone
            ? phone.startsWith(COUNTRY_SG_CODE)
              ? phone.split(COUNTRY_SG_CODE)[1].trim()
              : phone
            : "",
          whatsapp: whatsappFinally
            ? whatsappFinally.startsWith(COUNTRY_SG_CODE)
              ? whatsappFinally.split(COUNTRY_SG_CODE)[1].trim()
              : whatsappFinally
            : "",
          registrationNumber: config?.salesTeamInfo?.registrationNumber || "",
          photo: { url: photoArgs?.url || "" },
          photoId: photoId || "",
          adRerportUrl: profile?.adRerportUrl || "",
          enabledAutoAddNewProject: {
            enable: userFeatureAutoAddNewProject?.enabled || false,
            type: EFeatureType.AutoAddNewProject,
          },

          // headerScript: profile?.headerScript ?? "",
          // bodyScript: profile?.bodyScript ?? "",
          // enquiryScript: profile?.enquiryScript ?? "",
        });
      });
    } else {
      findUserDetail(id).then((res) => {
        if (!res?.data) return;
        const {
          firstName,
          lastName,
          phone,
          email,
          adRerportUrl,
          config,
          userFeatures,
          whatsapp,
          photo,
          photoId,
          role,
        } = res.data;

        setUserEdit(res.data);

        const features = userFeatures?.map((item) => item?.feature);
        const userHasDirectoryPermission =
          features &&
          !!findFeatureByType(features, EFeatureType.Directory)?.enabled;

        setUserHasDirectoryPermission(!!userHasDirectoryPermission);
        if (config) setOptionsSelect(config);

        const userFeatureAutoAddNewProject = userFeatures?.find(
          (userFeature) =>
            userFeature.feature.type === EFeatureType.AutoAddNewProject,
        );

        let photoArgs = {
          width: 0,
          url: "",
        };
        if (photo?.urls?.[0]) {
          photoArgs = photo?.urls?.[0];
        }
        const whatsappFinally =
          role === Roles.USER ? config?.whatsapp : whatsapp;
        setReset({
          firstName,
          lastName,
          password: "",
          email,
          phone: phone
            ? phone.startsWith(COUNTRY_SG_CODE)
              ? phone.split(COUNTRY_SG_CODE)[1].trim()
              : phone
            : "",
          whatsapp: whatsappFinally
            ? whatsappFinally.startsWith(COUNTRY_SG_CODE)
              ? whatsappFinally.split(COUNTRY_SG_CODE)[1].trim()
              : whatsappFinally
            : "",
          registrationNumber: config?.salesTeamInfo?.registrationNumber || "",
          photo:
            res.data.role === Roles.USER ? { url: photoArgs?.url || "" } : null,
          photoId: res.data.role === Roles.USER ? photoId || "" : "",
          adRerportUrl: adRerportUrl || "",
          enabledAutoAddNewProject: {
            enable: userFeatureAutoAddNewProject?.enabled || false,
            type: EFeatureType.AutoAddNewProject,
          },
        });
      });
    }
  }, []);

  return (
    <Form<EditUserSchema>
      resetValues={reset}
      onSubmit={onSubmit}
      validationSchema={editUserSchema as any}
      onInitForm={(methods) => {
        setForm(methods);
      }}
      className="grid grid-cols-1 h-full gap-6 @container md:grid-cols-2 [&_.rizzui-input-label]:font-medium [&_.rizzui-input-label]:text-gray-900"
    >
      {({ register, control, watch, formState: { errors } }) => {
        return (
          <div className="col-span-full flex flex-col gap-4">
            <div className="mt-4 flex items-center justify-between">
              <Title as="h5" className="@xl:basis-auto text-xl md:text-2xl">
                {title}
              </Title>
              {isOpen && (
                <ActionIcon size="sm" variant="text" onClick={closeModal}>
                  <PiXBold className="h-auto w-5" />
                </ActionIcon>
              )}
            </div>
            <div className="flex items-center justify-between">
              <div className="font-bold text-black">Full ID: {id}</div>
            </div>
            <div className="flex-1 flex-col gap-3 flex">
              <Controller
                control={control}
                name="email"
                render={({ field, fieldState }) => (
                  <Input
                    label="Email"
                    size="lg"
                    placeholder="Enter user's Email Address"
                    className="col-span-full"
                    {...field}
                    value={field.value}
                    error={fieldState.error?.message}
                    disabled
                  />
                )}
              />

              {/* <Controller
                control={control}
                name="phone"
                render={({ field, fieldState, formState }) => (
                  <Input
                    label="Phone Number"
                    size="lg"
                    placeholder="Enter phone number"
                    className="col-span-full"
                    {...field}

                    value={field.value}
                    error={fieldState.error?.message}
                  />
                )}
              /> */}

              {/* <Controller
                name={"phone"}
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Input
                    className="col-span-full"
                    size="lg"
                    label="Phone Number"
                    type="tel"
                    value={formatPhoneNumber(value)}
                    onChange={onChange}
                    // @ts-ignore
                    error={errors?.phone?.message as string}
                  />
                )}
              /> */}

              {/* <Input
                label="Phone number"
                size="lg"
                placeholder=""
                {...register("phone")}
                className="col-span-full"
                error={errors.phone?.message}
              /> */}

              <Controller
                control={control}
                name="phone"
                render={({ field, fieldState }) => (
                  <Input
                    label="Phone number"
                    type="text"
                    prefix="+65"
                    size="lg"
                    placeholder="Enter phone number"
                    className="col-span-full"
                    {...field}
                    value={formatPhoneNumber(field.value)}
                    error={fieldState.error?.message}
                  />
                )}
              />

              {!isMyProfile && userEdit?.role === Roles.USER && (
                <>
                  <Controller
                    control={control}
                    name="whatsapp"
                    render={({ field, fieldState }) => (
                      <Input
                        label="WhatsApp Number"
                        type="text"
                        prefix="+65"
                        size="lg"
                        placeholder="Enter WhatsApp number"
                        className="col-span-full"
                        {...field}
                        value={formatPhoneNumber(field.value || "")}
                        error={fieldState.error?.message}
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name="registrationNumber"
                    render={({ field, fieldState }) => (
                      <Input
                        label="CEA Registration Number"
                        type="text"
                        size="lg"
                        placeholder="Enter CEA registration number"
                        className="col-span-full"
                        {...field}
                        value={field.value || ""}
                        error={fieldState.error?.message}
                      />
                    )}
                  />
                </>
              )}

              {isMyProfile && profile?.role === Roles.USER && (
                <>
                  <Controller
                    control={control}
                    name="whatsapp"
                    render={({ field, fieldState }) => (
                      <Input
                        label="WhatsApp Number"
                        type="text"
                        prefix="+65"
                        size="lg"
                        placeholder="Enter WhatsApp number"
                        className="col-span-full"
                        {...field}
                        value={formatPhoneNumber(field.value || "")}
                        error={fieldState.error?.message}
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name="registrationNumber"
                    render={({ field, fieldState }) => (
                      <Input
                        label="CEA Registration Number"
                        type="text"
                        size="lg"
                        placeholder="Enter CEA registration number"
                        className="col-span-full"
                        {...field}
                        value={field.value || ""}
                        error={fieldState.error?.message}
                      />
                    )}
                  />
                </>
              )}

              {/* <Controller
                control={control}
                name="phone"
                render={({ field, fieldState, formState }) => (
                  <Input
                    type="text"
                    label="Phone Number"
                    {...field}

                    value={field.value}
                    error={fieldState.error?.message}
                  />
                )}
              /> */}

              {/* <Controller
                name={"phone"}
                control={control}
                render={({ field: { value, onChange } }) => (
                  <PhoneNumber
                    className="col-span-full"
                    size="lg"
                    label="Phone Number"
                    country="sg"
                    value={formatPhone(value, "+65")}
                    onChange={onChange}
                    // @ts-ignore
                    error={errors?.phone?.message as string}
                  />
                )}
              /> */}

              <div className="flex gap-3 w-full">
                <Input
                  label="First Name"
                  size="lg"
                  placeholder="Enter user's first name"
                  {...register("firstName")}
                  className="[&>label>span]:font-medium flex-1 basis-1/2"
                  error={errors.firstName?.message}
                />
                <Input
                  label="Last Name"
                  size="lg"
                  placeholder="Enter user's last name"
                  {...register("lastName")}
                  className="[&>label>span]:font-medium flex-1 basis-1/2"
                  error={errors.lastName?.message}
                />
              </div>

              {/* {!isMyProfile && mapOptions.length > 0 && (
                <Textarea
                  label="Header Script"
                  size="lg"
                  {...register("headerScript")}
                  rows={3}
                  className="[&>label>span]:font-medium"
                  error={form?.formState?.errors?.headerScript?.message}
                />
              )} */}

              {/* {!isMyProfile && mapOptions.length > 0 && (
                <Textarea
                  label="Body Script"
                  size="lg"
                  placeholder=""
                  {...register("bodyScript")}
                  className="[&>label>span]:font-medium "
                  error={errors.bodyScript?.message}
                />
              )} */}

              {/* {!isMyProfile && mapOptions.length > 0 && (
                <Textarea
                  label="Enquire Script"
                  size="lg"
                  {...register("enquiryScript")}
                  rows={3}
                  className="[&>label>span]:font-medium"
                  error={form?.formState?.errors?.enquiryScript?.message}
                />
              )} */}

              {!isMyProfile && profile?.role === Roles.ADMIN && (
                <Controller
                  control={control}
                  name="adRerportUrl"
                  render={({ field, fieldState }) => (
                    <Input
                      label="Ad Report URL"
                      size="lg"
                      placeholder="Enter Ad Report URL"
                      className="col-span-full"
                      {...field}
                      value={field.value}
                      error={fieldState.error?.message}
                    />
                  )}
                />
              )}

              {!isMyProfile &&
                profile?.role === Roles.ADMIN &&
                userHasDirectoryPermission && (
                  <>
                    <p>Custom Domain</p>
                    <SelectDomain
                      options={mapOptions}
                      defaultOptions={defaultOptions}
                      configID={configID || []}
                      reset={refetchDomains}
                    />
                  </>
                )}

              {/* {profile?.role !== Roles.ADMIN && (
                <div className="grid w-full items-start grid-cols-1">
                  <Controller
                    control={control}
                    name="enabledAutoAddNewProject.enable"
                    render={({ field, fieldState, formState }) => (
                      <>
                        <p className="text-sm text-gray-900 font-medium">
                          Auto Add New Project
                        </p>
                        <Switch
                          label="Enable"
                          size="lg"
                          className=" w-auto"
                          onChange={field.onChange}
                          // {...field}

                          checked={field.value}
                          error={fieldState.error?.message}
                        />
                      </>
                    )}
                  />
                </div>
              )} */}

              {isMyProfile && (
                <Select
                  placeholder="Select default page"
                  options={[
                    {
                      label: "All Leads",
                      value: EDefaultUserSettingRoute.ALL_LEADS,
                    },
                    {
                      label: "Dashboard",
                      value: EDefaultUserSettingRoute.DASHBOARD,
                    },
                  ]}
                  value={
                    defaultHomeRoute || {
                      label: "Dashboard",
                      value: EDefaultUserSettingRoute.DASHBOARD,
                    }
                  }
                  // displayValue={(value) => {
                  //   return "page"
                  //   if (value === EDefaultUserSettingRoute.ALL_LEADS) {
                  //     return "All Leads";
                  //   } else {
                  //     return "Dashboard";
                  //   }
                  // }}
                  onChange={(selected: {
                    label: string;
                    value: EDefaultUserSettingRoute;
                  }) => {
                    setDefaultHomeRoute(selected);
                    handleSetDefaultRoute(selected.value);
                  }}
                  label="Default Page"
                />
              )}

              {isMyProfile && (
                <Controller
                  control={control}
                  name="photo"
                  render={({ field: { onChange } }) => (
                    <SingleImageUpload
                      imageUrl={watch("photo")?.url || ""}
                      title="Avatar"
                      setValue={onChange}
                      uploaderText="Upload Avatar photo"
                      uploadContainerClass="w-full h-fit md:w-1/3"
                      imageContainerClassName="rounded-full w-[120px] h-[120px] p-0"
                      imageClassName="rounded-full w-[120px] h-[120px] p-0 object-cover"
                      toolbarContainerClassName="top-[20px] end-[10px] [&>div]:-translate-x-[10px]"
                      dropZoneClassName="w-[120px] h-[120px] rounded-full"
                      uploadDropzoneContainerClass="w-fit"
                    />
                  )}
                />
              )}
              {!isMyProfile && userEdit?.role === Roles.USER && (
                <Controller
                  control={control}
                  name="photo"
                  render={({ field: { onChange } }) => (
                    <SingleImageUpload
                      imageUrl={watch("photo")?.url || ""}
                      title="Avatar"
                      setValue={onChange}
                      uploaderText="Upload Avatar photo"
                      uploadContainerClass="w-full h-fit md:w-1/3"
                      imageContainerClassName="rounded-full w-[120px] h-[120px] p-0"
                      imageClassName="rounded-full w-[120px] h-[120px] p-0 object-cover"
                      toolbarContainerClassName="top-[20px] end-[10px] [&>div]:-translate-x-[10px]"
                      dropZoneClassName="w-[120px] h-[120px] rounded-full"
                      uploadDropzoneContainerClass="w-fit"
                    />
                  )}
                />
              )}
            </div>

            <div className="sticky bottom-0  py-4 px-2 -mx-4 border-t border-gray-200 bg-white z-10 @lg:gap-4 @xl:grid @xl:auto-cols-max @xl:grid-flow-col col-span-full flex items-center justify-end gap-4">
              {backTo && (
                <Button
                  variant="outline"
                  onClick={handleBack}
                  className="w-full @xl:w-auto"
                >
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                isLoading={isLoading}
                disabled={!form?.formState.isDirty}
                className="w-full @xl:w-auto"
              >
                Save
              </Button>
            </div>
          </div>
        );
      }}
    </Form>
  );
}
